/**
 * Lowered-IR transform passes — pure rewrites over LoweredNode / Statement /
 * Expression trees with no SSA knowledge. Run as a pipeline after SSA→statement
 * lowering to clean up and restructure the emitted pseudo-C.
 */

import { Expression, Statement, LoweredNode } from './ExpressionIR';
import { COMPARE_INVERT } from './SsaTypes';

/**
 * Hoists assignments that appear in both branches of an if/else to before the if.
 * When a variable is assigned different constant values (0/1) in each branch,
 * converts the pattern to a conditional expression: var = cond ? val1 : val2.
 */
export function hoistCommonAssigns(node: LoweredNode): LoweredNode {
  switch (node.kind) {
    case 'sequence':
      return hoistFromSequence(node);
    case 'if':
      return hoistFromIf(node);
    case 'while':
      return { kind: 'while', condition: node.condition, body: hoistCommonAssigns(node.body) };
    case 'do_while':
      return { kind: 'do_while', body: hoistCommonAssigns(node.body), condition: node.condition };
    case 'for':
      return { kind: 'for', init: node.init, condition: node.condition, increment: node.increment, body: hoistCommonAssigns(node.body) };
    case 'labeled_block':
      return { kind: 'labeled_block', label: node.label, body: hoistCommonAssigns(node.body) };
    case 'switch':
      return {
        kind: 'switch', selector: node.selector,
        cases: node.cases.map(caseEntry => ({ values: caseEntry.values, body: hoistCommonAssigns(caseEntry.body) })),
        defaultBody: hoistCommonAssigns(node.defaultBody),
      };
    default:
      return node;
  }
}

function hoistFromSequence(node: LoweredNode & { kind: 'sequence' }): LoweredNode {
  const children = node.children.map(child => hoistCommonAssigns(child));
  const result: LoweredNode[] = [];
  for (const child of children) {
    if (child.kind === 'if' && child.elseBody !== null) {
      const ifWithElse = child as LoweredNode & { kind: 'if'; elseBody: LoweredNode };
      const hoisted = tryHoistFromBranches(ifWithElse);
      if (hoisted) {
        for (const pre of hoisted.hoisted) { result.push(pre); }
        result.push(hoisted.remaining);
        continue;
      }
    }
    result.push(child);
  }
  if (result.length === 1) { return result[0]; }
  return { kind: 'sequence', children: result };
}

function hoistFromIf(node: LoweredNode & { kind: 'if' }): LoweredNode {
  const thenBody = hoistCommonAssigns(node.thenBody);
  const elseBody = node.elseBody ? hoistCommonAssigns(node.elseBody) : null;
  const ifNode: LoweredNode = { kind: 'if', condition: node.condition, thenBody, elseBody };
  if (elseBody === null) {
    return ifNode;
  }
  const ifWithElse = ifNode as LoweredNode & { kind: 'if'; elseBody: LoweredNode };
  const hoisted = tryHoistFromBranches(ifWithElse);
  if (!hoisted) {
    return ifNode;
  }
  const parts = [...hoisted.hoisted];
  if (!isEmptyLowered(hoisted.remaining)) {
    parts.push(hoisted.remaining);
  }
  if (parts.length === 1) { return parts[0]; }
  return { kind: 'sequence', children: parts };
}

interface HoistResult {
  hoisted: LoweredNode[];
  remaining: LoweredNode;
}

interface BranchBodies {
  thenBody: LoweredNode;
  elseBody: LoweredNode;
}

function tryHoistFromBranches(ifNode: LoweredNode & { kind: 'if'; elseBody: LoweredNode }): HoistResult | null {
  const hoisted: LoweredNode[] = [];
  const afterEqualAssigns = hoistEqualFirstAssigns(ifNode.thenBody, ifNode.elseBody, hoisted);
  const { thenBody, elseBody } = hoistConstTernaries(ifNode.condition, afterEqualAssigns.thenBody, afterEqualAssigns.elseBody, hoisted);

  if (hoisted.length === 0) {
    return null;
  }
  const thenEmpty = isEmptyLowered(thenBody);
  const elseEmpty = isEmptyLowered(elseBody);
  if (thenEmpty && elseEmpty) {
    return { hoisted, remaining: { kind: 'sequence', children: [] } };
  }
  const remaining: LoweredNode = { kind: 'if', condition: ifNode.condition, thenBody, elseBody: elseEmpty ? null : elseBody };
  return { hoisted, remaining };
}

// Strategy 1: hoist assignments that appear as the FIRST write to the same
// variable in both branches with the same value, so the order of side effects
// is unchanged.
function hoistEqualFirstAssigns(thenBody: LoweredNode, elseBody: LoweredNode, hoisted: LoweredNode[]): BranchBodies {
  const thenFirstAssigns = collectFirstAssignPerVar(thenBody);
  const elseFirstAssigns = collectFirstAssignPerVar(elseBody);
  for (const [target, thenStmt] of thenFirstAssigns) {
    const elseStmt = elseFirstAssigns.get(target);
    if (!elseStmt) { continue; }
    if (!expressionsEqual(thenStmt.value, elseStmt.value)) { continue; }
    hoisted.push({ kind: 'block', body: [thenStmt] });
    thenBody = removeFirstAssignTo(thenBody, target);
    elseBody = removeFirstAssignTo(elseBody, target);
  }
  return { thenBody, elseBody };
}

// Strategy 2: when both branches assign different small constants (0/1) to the
// same variable as their ONLY assignment to it, convert to a ternary.
function hoistConstTernaries(condition: Expression, thenBody: LoweredNode, elseBody: LoweredNode, hoisted: LoweredNode[]): BranchBodies {
  const thenOnlyAssigns = collectUniqueAssigns(thenBody);
  const elseOnlyAssigns = collectUniqueAssigns(elseBody);
  for (const [target, elseStmt] of elseOnlyAssigns) {
    const thenStmt = thenOnlyAssigns.get(target);
    if (!thenStmt) { continue; }
    if (!isSmallConst(thenStmt.value) || !isSmallConst(elseStmt.value)) { continue; }
    if (expressionsEqual(thenStmt.value, elseStmt.value)) { continue; }
    const ternary: Statement = {
      kind: 'assign',
      target: thenStmt.target,
      type: thenStmt.type,
      value: { kind: 'select', condition, trueVal: thenStmt.value, falseVal: elseStmt.value },
    };
    hoisted.push({ kind: 'block', body: [ternary] });
    thenBody = removeAssignTo(thenBody, target);
    elseBody = removeAssignTo(elseBody, target);
  }
  return { thenBody, elseBody };
}

/**
 * For each variable, find the first assignment in the node tree.
 * Only returns the first write to each variable (so hoisting is safe).
 */
function collectFirstAssignPerVar(node: LoweredNode): Map<string, Statement & { kind: 'assign' }> {
  const result = new Map<string, Statement & { kind: 'assign' }>();
  walkAssigns(node, (stmt) => {
    if (!result.has(stmt.target)) {
      result.set(stmt.target, stmt);
    }
  });
  return result;
}

function walkAssigns(node: LoweredNode, callback: (stmt: Statement & { kind: 'assign' }) => void): void {
  if (node.kind === 'block') {
    for (const stmt of node.body) {
      if (stmt.kind === 'assign') { callback(stmt); }
    }
  } else if (node.kind === 'sequence') {
    for (const child of node.children) { walkAssigns(child, callback); }
  }
  // Don't recurse into if/while/switch — those are conditional and not safe to hoist from
}

/**
 * Collects variables that are assigned exactly once in the node.
 * Returns a map of target → statement for variables with a single assignment.
 */
function collectUniqueAssigns(node: LoweredNode): Map<string, Statement & { kind: 'assign' }> {
  const all = new Map<string, { stmt: Statement & { kind: 'assign' }; count: number }>();
  collectAllAssigns(node, all);
  const result = new Map<string, Statement & { kind: 'assign' }>();
  for (const [target, entry] of all) {
    if (entry.count === 1) {
      result.set(target, entry.stmt);
    }
  }
  return result;
}

function collectAllAssigns(node: LoweredNode, out: Map<string, { stmt: Statement & { kind: 'assign' }; count: number }>): void {
  if (node.kind === 'block') {
    for (const stmt of node.body) {
      if (stmt.kind === 'assign') {
        const existing = out.get(stmt.target);
        if (existing) { existing.count++; }
        else { out.set(stmt.target, { stmt, count: 1 }); }
      }
    }
  } else if (node.kind === 'sequence') {
    for (const child of node.children) { collectAllAssigns(child, out); }
  }
}

function removeFirstAssignTo(node: LoweredNode, target: string): LoweredNode {
  if (node.kind === 'block') {
    for (let index = 0; index < node.body.length; index++) {
      if (node.body[index].kind === 'assign' && (node.body[index] as Statement & { kind: 'assign' }).target === target) {
        const remaining = [...node.body.slice(0, index), ...node.body.slice(index + 1)];
        if (remaining.length === 0) { return { kind: 'sequence', children: [] }; }
        return { kind: 'block', body: remaining };
      }
    }
  }
  if (node.kind === 'sequence') {
    for (let index = 0; index < node.children.length; index++) {
      const updated = removeFirstAssignTo(node.children[index], target);
      if (updated !== node.children[index]) {
        const children = [...node.children.slice(0, index), updated, ...node.children.slice(index + 1)].filter(child => !isEmptyLowered(child));
        if (children.length === 0) { return { kind: 'sequence', children: [] }; }
        if (children.length === 1) { return children[0]; }
        return { kind: 'sequence', children };
      }
    }
  }
  return node;
}

function removeAssignTo(node: LoweredNode, target: string): LoweredNode {
  if (node.kind === 'block') {
    const filtered = node.body.filter(stmt => !(stmt.kind === 'assign' && stmt.target === target));
    if (filtered.length === 0) { return { kind: 'sequence', children: [] }; }
    return { kind: 'block', body: filtered };
  }
  if (node.kind === 'sequence') {
    const children = node.children.map(child => removeAssignTo(child, target)).filter(child => !isEmptyLowered(child));
    if (children.length === 0) { return { kind: 'sequence', children: [] }; }
    if (children.length === 1) { return children[0]; }
    return { kind: 'sequence', children };
  }
  return node;
}

function expressionsEqual(expressionA: Expression, expressionB: Expression): boolean {
  if (expressionA.kind !== expressionB.kind) { return false; }
  if (expressionA.kind === 'const' && expressionB.kind === 'const') {
    return expressionA.value === expressionB.value;
  }
  if (expressionA.kind === 'var' && expressionB.kind === 'var') {
    return expressionA.name === expressionB.name;
  }
  return false;
}

function isSmallConst(expression: Expression): boolean {
  return expression.kind === 'const' && typeof expression.value === 'number' && expression.value >= 0 && expression.value <= 1;
}

// Conservative: only catches intra-block dead assigns where the target is
// overwritten before any subsequent read within the same block.
export function eliminateDeadAssigns(node: LoweredNode): LoweredNode {
  switch (node.kind) {
    case 'sequence':
      return { kind: 'sequence', children: node.children.map(child => eliminateDeadAssigns(child)) };
    case 'block':
      return { kind: 'block', body: filterDeadAssigns(node.body) };
    case 'if':
      return {
        kind: 'if', condition: node.condition,
        thenBody: eliminateDeadAssigns(node.thenBody),
        elseBody: node.elseBody ? eliminateDeadAssigns(node.elseBody) : null,
      };
    case 'while':
      return { kind: 'while', condition: node.condition, body: eliminateDeadAssigns(node.body) };
    case 'do_while':
      return { kind: 'do_while', body: eliminateDeadAssigns(node.body), condition: node.condition };
    case 'labeled_block':
      return { kind: 'labeled_block', label: node.label, body: eliminateDeadAssigns(node.body) };
    case 'switch':
      return {
        kind: 'switch', selector: node.selector,
        cases: node.cases.map(caseEntry => ({ values: caseEntry.values, body: eliminateDeadAssigns(caseEntry.body) })),
        defaultBody: eliminateDeadAssigns(node.defaultBody),
      };
    default:
      return node;
  }
}

function filterDeadAssigns(body: Statement[]): Statement[] {
  const filtered: Statement[] = [];
  for (let index = 0; index < body.length; index++) {
    const current = body[index];
    // Skip self-assignments (var0 = var0).
    if (current.kind === 'assign' && current.value.kind === 'var' && current.value.name === current.target) {
      continue;
    }
    // Skip dead assigns: target overwritten before next read, with no side effects.
    if (current.kind === 'assign' && !expressionHasSideEffect(current.value) && isOverwrittenBeforeRead(body, index, current.target)) {
      continue;
    }
    filtered.push(current);
  }
  return filtered;
}

// True when `target` is reassigned later in the block before any statement reads
// it. A read (even by a statement that also reassigns it, e.g. `x = x`) keeps the
// original assign live.
function isOverwrittenBeforeRead(body: Statement[], assignIndex: number, target: string): boolean {
  for (let scanIndex = assignIndex + 1; scanIndex < body.length; scanIndex++) {
    const later = body[scanIndex];
    if (statementReadsVariable(later, target)) {
      return false;
    }
    if (later.kind === 'assign' && later.target === target) {
      return true;
    }
  }
  return false;
}

function expressionHasSideEffect(expression: Expression): boolean {
  switch (expression.kind) {
    case 'call':
    case 'call_indirect':
      return true;
    case 'binary':
      return expressionHasSideEffect(expression.left) || expressionHasSideEffect(expression.right);
    case 'unary':
      return expressionHasSideEffect(expression.operand);
    case 'compare':
      return expressionHasSideEffect(expression.left) || expressionHasSideEffect(expression.right);
    case 'select':
      return expressionHasSideEffect(expression.condition) ||
        expressionHasSideEffect(expression.trueVal) || expressionHasSideEffect(expression.falseVal);
    case 'load':
      return false;
    case 'convert':
      return expressionHasSideEffect(expression.operand);
    case 'field_access':
      return expressionHasSideEffect(expression.base);
    default:
      return false;
  }
}

function extractConditionVariables(condition: Expression): string[] {
  const variables: string[] = [];
  function collect(expression: Expression): void {
    if (expression.kind === 'var') {
      variables.push(expression.name);
    } else if (expression.kind === 'binary' || expression.kind === 'compare') {
      collect(expression.left);
      collect(expression.right);
    } else if (expression.kind === 'unary') {
      collect(expression.operand);
    } else if (expression.kind === 'field_access') {
      collect(expression.base);
    }
  }
  collect(condition);
  return variables;
}

function extractAssignTo(node: LoweredNode, variableName: string): { statement: Statement & { kind: 'assign' }; remaining: LoweredNode | null } | null {
  if (node.kind === 'block') {
    for (let statementIndex = node.body.length - 1; statementIndex >= 0; statementIndex--) {
      const statement = node.body[statementIndex];
      if (statement.kind === 'assign' && statement.target === variableName) {
        const remainingBody = [...node.body.slice(0, statementIndex), ...node.body.slice(statementIndex + 1)];
        const remaining = remainingBody.length > 0 ? { kind: 'block' as const, body: remainingBody } : null;
        return { statement, remaining };
      }
      // Stop if this statement reads the variable (can't move init past a read)
      if (statementReadsVariable(statement, variableName)) {
        break;
      }
    }
  }
  if (node.kind === 'sequence' && node.children.length > 0) {
    // Try the last child first
    const lastChild = node.children[node.children.length - 1];
    const fromLast = extractAssignTo(lastChild, variableName);
    if (fromLast) {
      const newChildren = fromLast.remaining
        ? [...node.children.slice(0, -1), fromLast.remaining]
        : node.children.slice(0, -1);
      const remaining = newChildren.length > 0
        ? { kind: 'sequence' as const, children: newChildren }
        : null;
      return { statement: fromLast.statement, remaining };
    }
  }
  return null;
}

function removeTrailingContinue(node: LoweredNode): LoweredNode {
  if (node.kind === 'sequence' && node.children.length > 0) {
    const lastIndex = node.children.length - 1;
    if (node.children[lastIndex].kind === 'continue') {
      if (node.children.length === 1) { return { kind: 'sequence', children: [] }; }
      return { kind: 'sequence', children: node.children.slice(0, -1) };
    }
    const lastCleaned = removeTrailingContinue(node.children[lastIndex]);
    if (lastCleaned !== node.children[lastIndex]) {
      return { kind: 'sequence', children: [...node.children.slice(0, -1), lastCleaned] };
    }
  }
  if (node.kind === 'continue') {
    return { kind: 'sequence', children: [] };
  }
  return node;
}

function statementReadsVariable(statement: Statement, variableName: string): boolean {
  switch (statement.kind) {
    case 'assign':
      return expressionReferences(statement.value, variableName);
    case 'store':
      return expressionReferences(statement.address, variableName) || expressionReferences(statement.value, variableName);
    case 'call':
      return statement.args.some(arg => expressionReferences(arg, variableName));
    case 'call_indirect':
      return expressionReferences(statement.tableIndex, variableName) ||
        statement.args.some(arg => expressionReferences(arg, variableName));
    case 'global_set':
      return expressionReferences(statement.value, variableName);
    case 'return':
      return statement.values.some(value => expressionReferences(value, variableName));
    case 'expr':
      return expressionReferences(statement.value, variableName);
    default:
      return false;
  }
}

/**
 * Reduces nesting depth by converting deep if-bodies into guard clauses.
 * Inside loops: `if (cond) { large_body }` → `if (!cond) continue; large_body`
 * Top-level: `if (cond) { rest }` → `if (!cond) return; rest`
 * Nested ifs: `if (a) { if (b) { body } }` → `if (a && b) { body }` (no-else only)
 */
export function reduceNesting(node: LoweredNode, insideLoop: boolean = false): LoweredNode {
  switch (node.kind) {
    case 'sequence': {
      const children = node.children.map(child => reduceNesting(child, insideLoop));
      const flattened = flattenGuardClauses(children, insideLoop);
      if (flattened.length === 1) { return flattened[0]; }
      return { kind: 'sequence', children: flattened };
    }
    case 'if': {
      const thenBody = reduceNesting(node.thenBody, insideLoop);
      const elseBody = node.elseBody ? reduceNesting(node.elseBody, insideLoop) : null;
      // Merge nested if: if (a) { if (b) { body } } → if (a && b) { body }
      if (!elseBody && thenBody.kind === 'if' && !thenBody.elseBody) {
        const merged: Expression = { kind: 'binary', op: '&&', left: node.condition, right: thenBody.condition };
        return { kind: 'if', condition: merged, thenBody: thenBody.thenBody, elseBody: null };
      }
      return { kind: 'if', condition: node.condition, thenBody, elseBody };
    }
    case 'while': {
      const body = reduceNesting(node.body, true);
      const reduced = reduceLoopBody(body);
      return { kind: 'while', condition: node.condition, body: reduced };
    }
    case 'do_while': {
      const body = reduceNesting(node.body, true);
      return { kind: 'do_while', body: reduceLoopBody(body), condition: node.condition };
    }
    case 'for':
      return { kind: 'for', init: node.init, condition: node.condition, increment: node.increment, body: reduceNesting(node.body, true) };
    case 'labeled_block':
      return { kind: 'labeled_block', label: node.label, body: reduceNesting(node.body, insideLoop) };
    case 'switch':
      return {
        kind: 'switch', selector: node.selector,
        cases: node.cases.map(caseEntry => ({ values: caseEntry.values, body: reduceNesting(caseEntry.body) })),
        defaultBody: reduceNesting(node.defaultBody),
      };
    default:
      return node;
  }
}

/**
 * Inside a loop body, if the entire body is wrapped in a single if-without-else,
 * flip to a guard clause: `if (!cond) continue; body_contents`
 */
function reduceLoopBody(body: LoweredNode): LoweredNode {
  const children = getChildren(body);
  if (children.length === 1 && children[0].kind === 'if' && !children[0].elseBody) {
    const guardCondition = negateExpression(children[0].condition);
    const guardClause: LoweredNode = { kind: 'if', condition: guardCondition, thenBody: { kind: 'continue' }, elseBody: null };
    const innerChildren = getChildren(children[0].thenBody);
    return { kind: 'sequence', children: [guardClause, ...innerChildren] };
  }
  return body;
}

/**
 * In a sequence, when an if-without-else ends with return/break/continue,
 * pull the subsequent siblings out of the nesting.
 */
function flattenGuardClauses(children: LoweredNode[], insideLoop: boolean): LoweredNode[] {
  const result: LoweredNode[] = [];
  for (let index = 0; index < children.length; index++) {
    const child = children[index];
    // If this is an if-without-else that ends with an exit, and there are more siblings after,
    // it's already a guard clause — no change needed.
    // But if this is an if-without-else wrapping remaining siblings, convert to guard.
    if (child.kind === 'if' && !child.elseBody && index === children.length - 1) {
      // Last child is an if wrapping everything — check if it's worth flipping
      const innerChildren = getChildren(child.thenBody);
      if (innerChildren.length >= 2) {
        const guardCondition = negateExpression(child.condition);
        const exitNode: LoweredNode = insideLoop ? { kind: 'continue' } : { kind: 'return', values: [] };
        result.push({ kind: 'if', condition: guardCondition, thenBody: exitNode, elseBody: null });
        result.push(...innerChildren);
        continue;
      }
    }
    result.push(child);
  }
  return result;
}

function getChildren(node: LoweredNode): LoweredNode[] {
  if (node.kind === 'sequence') { return node.children; }
  return [node];
}

export function cleanupLowered(node: LoweredNode): LoweredNode {
  switch (node.kind) {
    case 'sequence': {
      const cleaned: LoweredNode[] = [];
      for (const child of node.children) {
        const result = cleanupLowered(child);
        if (!isEmptyLowered(result)) {
          cleaned.push(result);
        }
      }
      if (cleaned.length === 0) { return { kind: 'sequence', children: [] }; }
      if (cleaned.length === 1) { return cleaned[0]; }
      return { kind: 'sequence', children: cleaned };
    }
    case 'if': {
      const thenBody = cleanupLowered(node.thenBody);
      const elseBody = node.elseBody ? cleanupLowered(node.elseBody) : null;
      if (isEmptyLowered(thenBody) && elseBody && !isEmptyLowered(elseBody)) {
        return { kind: 'if', condition: negateExpression(node.condition), thenBody: elseBody, elseBody: null };
      }
      if (isEmptyLowered(thenBody) && (!elseBody || isEmptyLowered(elseBody))) {
        return { kind: 'sequence', children: [] };
      }
      if (elseBody && isEmptyLowered(elseBody)) {
        return { kind: 'if', condition: node.condition, thenBody, elseBody: null };
      }
      return { kind: 'if', condition: node.condition, thenBody, elseBody };
    }
    case 'while':
      return { kind: 'while', condition: node.condition, body: removeTrailingContinue(cleanupLowered(node.body)) };
    case 'do_while':
      return { kind: 'do_while', body: removeTrailingContinue(cleanupLowered(node.body)), condition: node.condition };
    case 'labeled_block':
      return { kind: 'labeled_block', label: node.label, body: cleanupLowered(node.body) };
    case 'switch':
      return {
        kind: 'switch',
        selector: node.selector,
        cases: node.cases.map(caseEntry => ({ values: caseEntry.values, body: cleanupLowered(caseEntry.body) })),
        defaultBody: cleanupLowered(node.defaultBody),
      };
    default:
      return node;
  }
}

function isEmptyLowered(node: LoweredNode): boolean {
  if (node.kind === 'sequence' && node.children.length === 0) { return true; }
  if (node.kind === 'block' && node.body.length === 0) { return true; }
  return false;
}

export function negateExpression(expression: Expression): Expression {
  if (expression.kind === 'compare') {
    const inverted = COMPARE_INVERT[expression.op];
    if (inverted) {
      return { kind: 'compare', op: inverted, left: expression.left, right: expression.right };
    }
  }
  if (expression.kind === 'unary' && expression.op === '!') {
    return expression.operand;
  }
  return { kind: 'unary', op: '!', operand: expression };
}

// Detects for-loop patterns at one nesting level (sequence → while pairs).
// Nested for-loops inside the body are detected by recursive descent but
// the init/increment extraction only looks at direct children of a sequence.
export function detectForLoops(node: LoweredNode): LoweredNode {
  switch (node.kind) {
    case 'sequence':
      return detectForLoopsInSequence(node);
    case 'if':
      return {
        kind: 'if',
        condition: node.condition,
        thenBody: detectForLoops(node.thenBody),
        elseBody: node.elseBody ? detectForLoops(node.elseBody) : null,
      };
    case 'while':
      return { kind: 'while', condition: node.condition, body: detectForLoops(node.body) };
    case 'do_while':
      return { kind: 'do_while', body: detectForLoops(node.body), condition: node.condition };
    case 'labeled_block':
      return { kind: 'labeled_block', label: node.label, body: detectForLoops(node.body) };
    case 'switch':
      return {
        kind: 'switch',
        selector: node.selector,
        cases: node.cases.map(caseEntry => ({ values: caseEntry.values, body: detectForLoops(caseEntry.body) })),
        defaultBody: detectForLoops(node.defaultBody),
      };
    default:
      return node;
  }
}

function detectForLoopsInSequence(node: LoweredNode & { kind: 'sequence' }): LoweredNode {
  const transformed = node.children.map(child => detectForLoops(child));
  const result: LoweredNode[] = [];
  for (let index = 0; index < transformed.length; index++) {
    const current = transformed[index];
    const next = transformed[index + 1];
    if (next && next.kind === 'while' && next.condition) {
      const forLoopNodes = tryBuildForLoop(current, next, next.condition);
      if (forLoopNodes) {
        for (const forLoopNode of forLoopNodes) { result.push(forLoopNode); }
        index++;
        continue;
      }
    }
    result.push(current);
  }
  if (result.length === 1) {
    return result[0];
  }
  return { kind: 'sequence', children: result };
}

// Tries to fuse a preceding init block with a following while into a for-loop,
// using each condition variable as a candidate counter. Returns the nodes to
// emit (leftover init statements, then the for-loop), or null if nothing matched.
function tryBuildForLoop(precedingNode: LoweredNode, whileNode: LoweredNode & { kind: 'while' }, condition: Expression): LoweredNode[] | null {
  for (const counterName of extractConditionVariables(condition)) {
    const incrementStatement = extractTrailingIncrement(whileNode.body, counterName);
    if (!incrementStatement) { continue; }
    const initResult = extractAssignTo(precedingNode, counterName);
    if (!initResult) { continue; }
    const bodyWithoutIncrement = removeIncrementAndContinue(whileNode.body, counterName);
    const nodes: LoweredNode[] = [];
    if (initResult.remaining) {
      nodes.push(initResult.remaining);
    }
    nodes.push(detectForLoops({
      kind: 'for',
      init: initResult.statement,
      condition,
      increment: incrementStatement,
      body: detectForLoops(bodyWithoutIncrement),
    }));
    return nodes;
  }
  return null;
}

function extractTrailingIncrement(node: LoweredNode, variableName: string): Statement & { kind: 'assign' } | null {
  if (node.kind === 'block' && node.body.length > 0) {
    const lastStatement = node.body[node.body.length - 1];
    if (lastStatement.kind === 'assign' && lastStatement.target === variableName) {
      return lastStatement;
    }
  }
  if (node.kind === 'sequence' && node.children.length > 0) {
    // Skip trailing continue/break — the increment is before them
    let lastIndex = node.children.length - 1;
    while (lastIndex >= 0 && (node.children[lastIndex].kind === 'continue' || node.children[lastIndex].kind === 'break')) {
      lastIndex--;
    }
    if (lastIndex >= 0) {
      return extractTrailingIncrement(node.children[lastIndex], variableName);
    }
  }
  return null;
}

function removeIncrementAndContinue(node: LoweredNode, variableName: string): LoweredNode {
  if (node.kind === 'sequence') {
    const filtered: LoweredNode[] = [];
    for (const child of node.children) {
      if (child.kind === 'continue') { continue; }
      if (child.kind === 'block') {
        const withoutIncrement = removeTrailingAssignTo(child, variableName);
        if (withoutIncrement) {
          filtered.push(withoutIncrement);
        }
      } else {
        filtered.push(child);
      }
    }
    if (filtered.length === 0) { return { kind: 'sequence', children: [] }; }
    if (filtered.length === 1) { return filtered[0]; }
    return { kind: 'sequence', children: filtered };
  }
  if (node.kind === 'block') {
    return removeTrailingAssignTo(node, variableName) || { kind: 'sequence', children: [] };
  }
  if (node.kind === 'continue') {
    return { kind: 'sequence', children: [] };
  }
  if (node.kind === 'if') {
    return {
      kind: 'if',
      condition: node.condition,
      thenBody: removeIncrementAndContinue(node.thenBody, variableName),
      elseBody: node.elseBody ? removeIncrementAndContinue(node.elseBody, variableName) : null,
    };
  }
  if (node.kind === 'for') {
    return {
      kind: 'for',
      init: node.init,
      condition: node.condition,
      increment: node.increment,
      body: removeIncrementAndContinue(node.body, variableName),
    };
  }
  return node;
}

function removeTrailingAssignTo(node: LoweredNode & { kind: 'block' }, variableName: string): LoweredNode | null {
  const lastStatement = node.body[node.body.length - 1];
  if (lastStatement && lastStatement.kind === 'assign' && lastStatement.target === variableName) {
    if (node.body.length <= 1) { return null; }
    return { kind: 'block', body: node.body.slice(0, -1) };
  }
  return node;
}

/**
 * Recovers a readable `while (cond)` from the faithful header-tested loop the structurer emits.
 *
 * A loop whose header computes its own test is structured as `while (true) { <header>; if (exit)
 * break; <body> }` — the header must run before the test each iteration, so the test cannot be
 * the `while` condition. Once the header inlines to nothing (the optimized case where the test is
 * a plain expression), the break-guard becomes the loop's first statement and the loop is exactly
 * `while (!exit) { body }`; fold it so. When the header leaves statements before the guard (the
 * low-optimization case where the test is stored to a local), the guard is not first and the loop
 * correctly stays an explicit `while (true)` with the test after the header.
 */
export function recoverWhileConditions(node: LoweredNode): LoweredNode {
  switch (node.kind) {
    case 'sequence': return { kind: 'sequence', children: node.children.map(recoverWhileConditions) };
    case 'if': return { kind: 'if', condition: node.condition, thenBody: recoverWhileConditions(node.thenBody), elseBody: node.elseBody ? recoverWhileConditions(node.elseBody) : null };
    case 'do_while': return { kind: 'do_while', body: recoverWhileConditions(node.body), condition: node.condition };
    case 'for': return { kind: 'for', init: node.init, condition: node.condition, increment: node.increment, body: recoverWhileConditions(node.body) };
    case 'switch': return { kind: 'switch', selector: node.selector, cases: node.cases.map(caseEntry => ({ values: caseEntry.values, body: recoverWhileConditions(caseEntry.body) })), defaultBody: recoverWhileConditions(node.defaultBody) };
    case 'labeled_block': return { kind: 'labeled_block', label: node.label, body: recoverWhileConditions(node.body) };
    case 'while': {
      const body = recoverWhileConditions(node.body);
      if (node.condition === null) {
        const guard = leadingBreakGuard(body);
        if (guard) {
          return { kind: 'while', condition: negateExpression(guard.exitCondition), body: guard.rest };
        }
      }
      return { kind: 'while', condition: node.condition, body };
    }
    default: return node;
  }
}

// When a loop body begins (after any empty blocks left by inlining) with `if (cond) break;`,
// returns that exit condition and the rest of the body; otherwise null.
function leadingBreakGuard(body: LoweredNode): { exitCondition: Expression; rest: LoweredNode } | null {
  const children = body.kind === 'sequence' ? body.children : [body];
  let index = 0;
  while (index < children.length && isEmptyLowered(children[index])) {
    index++;
  }
  if (index >= children.length) {
    return null;
  }
  const first = children[index];
  if (first.kind === 'if' && first.elseBody === null && isSingleBreak(first.thenBody)) {
    return { exitCondition: first.condition, rest: collapseSequence(children.slice(index + 1)) };
  }
  return null;
}

function isSingleBreak(node: LoweredNode): boolean {
  if (node.kind === 'break') {
    return true;
  }
  if (node.kind === 'sequence') {
    const meaningful = node.children.filter(child => !isEmptyLowered(child));
    return meaningful.length === 1 && isSingleBreak(meaningful[0]);
  }
  return false;
}

function collapseSequence(children: LoweredNode[]): LoweredNode {
  const filtered = children.filter(child => !isEmptyLowered(child));
  if (filtered.length === 0) { return { kind: 'sequence', children: [] }; }
  if (filtered.length === 1) { return filtered[0]; }
  return { kind: 'sequence', children: filtered };
}

function expressionReferences(expression: Expression, variableName: string): boolean {
  switch (expression.kind) {
    case 'var':
      return expression.name === variableName;
    case 'binary':
      return expressionReferences(expression.left, variableName) || expressionReferences(expression.right, variableName);
    case 'unary':
      return expressionReferences(expression.operand, variableName);
    case 'compare':
      return expressionReferences(expression.left, variableName) || expressionReferences(expression.right, variableName);
    case 'call':
      return expression.args.some(arg => expressionReferences(arg, variableName));
    case 'select':
      return expressionReferences(expression.condition, variableName) ||
        expressionReferences(expression.trueVal, variableName) ||
        expressionReferences(expression.falseVal, variableName);
    case 'load':
      return expressionReferences(expression.address, variableName);
    case 'convert':
      return expressionReferences(expression.operand, variableName);
    case 'field_access':
      return expressionReferences(expression.base, variableName);
    default:
      return false;
  }
}
